import {diApiEu} from '@di-shared/api/di';
import type {TripHistory} from '@di-shared/api/di/__generated_eu';
import {TripOrderByType} from '@di-shared/api/di/type';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseInfiniteQuery} from '@tanstack/react-query';

export default function useTripDetailEu({
  vin,
  selectedDate,
  orderBy = TripOrderByType.DESC,
}: {
  vin: string;
  selectedDate: Date;
  orderBy?: TripOrderByType;
}) {
  const params = {
    vin,
    date: getYYYYMMddFormat(selectedDate),
    orderBy,
    displayOffsetDateTime: true,
    paging: true,
    size: 3,
  };

  const {data, fetchNextPage, hasNextPage, isFetchingNextPage} = useSuspenseInfiniteQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveDailyTripHistory), params],
    queryFn: async ({pageParam = 1}) => {
      return diApiEu.drivingHistory.retrieveDailyTripHistory({page: pageParam, ...params});
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPage, lastPageParam) => {
      return lastPage.data.pageInfo.currentPage < lastPage.data.pageInfo.totalPages ? lastPageParam + 1 : undefined;
    },
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.data),
    }),
  });

  const isEmptyTrip = data.pages[0].content.length === 0;
  const firstPageData = data.pages[0];
  const totalDrivingHours = Math.floor(firstPageData.summary.totalDrvTime / 60);
  const totalDrivingMinutes = firstPageData.summary.totalDrvTime % 60;

  const tripDetails = data.pages.flatMap((trip) => trip.content);
  const formattedTripDetails = tripDetails.map((trip) => ({
    ...trip,
    intervalIgnitionHours: Math.floor(trip.drvTime / 60),
    intervalIgnitionMinutes: trip.drvTime % 60,
    isDrivingLessThanOneMinute: trip.drvTime === 0,
  }));

  const formattedRecentTripDetail: TripHistory & {
    intervalIgnitionHours: number;
    intervalIgnitionMinutes: number;
    isDrivingLessThanOneMinute: boolean;
  } = formattedTripDetails[0];

  return {
    tripDetailData: data.pages[0],
    isEmptyTrip,
    totalDrivingHours,
    totalDrivingMinutes,
    formattedTripDetails,
    formattedRecentTripDetail,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}
